import { useMutation } from "@tanstack/react-query";
import { Link, useParams } from "wouter";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Download,
  Loader2,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/Cards";
import { Button } from "@/components/ui/Buttons";
import DeadlineBanner from "@/components/DeadlineBanner";
import NextStepFooter from "@/components/NextStepFooter";
import { useProject } from "@/hooks/useProjects";
import { useSections, useProjectCoverage } from "@/hooks/useSections";
import { useMatrixItems } from "@/hooks/useProjects";
import { api, type ProposalSection } from "@/lib/api";

/**
 * Pre-flight check before submission. Each prerequisite gets a traffic light:
 *   GREEN  — done
 *   YELLOW — done with caveats / partial
 *   RED    — missing
 *
 * The export button at the top is the obvious move when everything is green;
 * still allowed when not, with a warning that gaps will export as
 * placeholders.
 */
export default function FinalCheck() {
  const params = useParams<{ id: string }>();
  const projectId = params.id;

  const project = useProject(projectId ?? null);
  const sections = useSections(projectId ?? null);
  const matrix = useMatrixItems(projectId ?? null);
  const coverage = useProjectCoverage(projectId ?? null);

  const exportMut = useMutation({
    mutationFn: (): Promise<{ fileId: string; downloadUrl: string }> =>
      api.exportProject(projectId!, "docx"),
    onSuccess(result) {
      window.open(result.downloadUrl, "_blank", "noopener");
      toast.success("Export ready — opened in a new tab.");
    },
    onError(err) {
      toast.error((err as Error).message);
    },
  });

  if (project.isLoading || sections.isLoading || matrix.isLoading) {
    return (
      <Layout projectId={projectId}>
        <PageHeader title="Final Check" subtitle="Loading…" />
        <div className="flex items-center justify-center py-24">
          <Loader2 size={20} className="animate-spin text-orange-400" />
        </div>
      </Layout>
    );
  }

  if (!project.data) {
    return (
      <Layout projectId={projectId}>
        <PageHeader title="Final Check" subtitle="Couldn't load" />
      </Layout>
    );
  }

  const sec = sections.data ?? [];
  const mat = matrix.data ?? [];
  const cov = coverage.data;

  const checks = computeChecks({
    project: project.data,
    sections: sec,
    matrixItems: mat,
    coverage: cov,
  });

  const greens = checks.filter((c) => c.tone === "ok").length;
  const yellows = checks.filter((c) => c.tone === "warn").length;
  const reds = checks.filter((c) => c.tone === "red").length;

  const allClear = reds === 0 && yellows === 0;

  return (
    <Layout projectId={projectId}>
      <DeadlineBanner project={project.data} />
      <PageHeader
        title="Final Check"
        subtitle={
          allClear
            ? "Everything's ready — export and submit."
            : `${reds} blocker${reds === 1 ? "" : "s"} · ${yellows} caution${yellows === 1 ? "" : "s"} · ${greens} ready`
        }
        actions={
          <Button
            variant="primary"
            size="sm"
            icon={<Download size={14} />}
            onClick={() => exportMut.mutate()}
            loading={exportMut.isPending}
            title={
              allClear ? "Generate the DOCX" : "Export anyway — gaps will appear as placeholders"
            }
          >
            Export DOCX
          </Button>
        }
      />

      <div className="p-8 pb-24 space-y-4 max-w-3xl">
        <Card>
          <div className="flex items-start gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{
                backgroundColor: allClear ? "rgba(34,197,94,0.1)" : "rgba(249,115,22,0.1)",
              }}
            >
              <ShieldCheck size={16} style={{ color: allClear ? "#22c55e" : "#F97316" }} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-white">
                {allClear ? "Ready to submit" : "Run through the list before exporting"}
              </h3>
              <p className="text-xs mt-0.5" style={{ color: "#9CA3AF" }}>
                Each item is checked against the project state. Click an item to jump to where the
                work happens.
              </p>
            </div>
          </div>
        </Card>

        <div className="space-y-2">
          {checks.map((c) => (
            <ChecklistRow key={c.id} check={c} projectId={projectId!} />
          ))}
        </div>
      </div>

      <NextStepFooter currentStep="qa" projectId={projectId!} />
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Checks
// ---------------------------------------------------------------------------

interface Check {
  id: string;
  title: string;
  detail: string;
  tone: "ok" | "warn" | "red";
  href?: string;
}

function computeChecks(args: {
  project: {
    summary: unknown;
    submissionFormat?: string | null | undefined;
    rawRfpText: string | null;
  };
  sections: ProposalSection[];
  matrixItems: { id: string; type: string; status: string; notApplicable: boolean }[];
  coverage:
    | {
        deliverable: number;
        deliverableCovered: number;
      }
    | undefined;
}): Check[] {
  const { project, sections, matrixItems, coverage } = args;
  const out: Check[] = [];

  // RFP parsed
  out.push({
    id: "rfp-parsed",
    title: "RFP parsed",
    detail: project.summary
      ? "Summary and requirements extracted."
      : project.rawRfpText
        ? "Raw text loaded but summary not yet generated."
        : "RFP source not loaded.",
    tone: project.summary ? "ok" : project.rawRfpText ? "warn" : "red",
    href: "summary",
  });

  // Submission format chosen
  out.push({
    id: "format",
    title: "Submission format chosen",
    detail: project.submissionFormat
      ? `Set to ${formatLabel(project.submissionFormat)}.`
      : "Pick Word doc, presentation, or both.",
    tone: project.submissionFormat ? "ok" : "red",
    href: "drafts",
  });

  // Outline approved
  out.push({
    id: "outline",
    title: "Response outline approved",
    detail:
      sections.length === 0
        ? "No sections yet. Approve an outline to start composing."
        : `${sections.length} section${sections.length === 1 ? "" : "s"} in the response.`,
    tone: sections.length > 0 ? "ok" : "red",
    href: "drafts",
  });

  // Drafts complete
  if (sections.length > 0) {
    const proseSections = sections.filter(
      (s) => s.kind === "prose_ai" || s.kind === "prose_external",
    );
    const missingProse = proseSections.filter(
      (s) => !s.draftText || s.draftText.trim().length < 20,
    );
    out.push({
      id: "drafts",
      title: "Prose sections drafted",
      detail:
        missingProse.length === 0
          ? `All ${proseSections.length} prose section${proseSections.length === 1 ? "" : "s"} have drafts.`
          : `${missingProse.length} of ${proseSections.length} prose section${proseSections.length === 1 ? "" : "s"} still missing prose.`,
      tone: missingProse.length === 0 ? "ok" : "red",
      href: "drafts",
    });

    // Asset slots filled
    const assetSections = sections.filter((s) =>
      ["designer_asset", "attachment", "pricing", "prose_external"].includes(s.kind),
    );
    if (assetSections.length > 0) {
      const missingAsset = assetSections.filter((s) => !s.assetFileId);
      out.push({
        id: "assets",
        title: "Asset slots filled",
        detail:
          missingAsset.length === 0
            ? `All ${assetSections.length} asset slot${assetSections.length === 1 ? "" : "s"} have files.`
            : `${missingAsset.length} of ${assetSections.length} asset slot${assetSections.length === 1 ? "" : "s"} still empty.`,
        tone: missingAsset.length === 0 ? "ok" : "red",
        href: "drafts",
      });
    }

    // All sections final
    const notFinal = sections.filter((s) => s.status !== "complete");
    out.push({
      id: "finalized",
      title: "All sections marked Final",
      detail:
        notFinal.length === 0
          ? "Every section is signed off."
          : `${notFinal.length} of ${sections.length} section${sections.length === 1 ? "" : "s"} still in draft or review.`,
      tone: notFinal.length === 0 ? "ok" : "warn",
      href: "drafts",
    });
  }

  // Every requirement covered
  if (coverage && coverage.deliverable > 0) {
    const uncovered = coverage.deliverable - coverage.deliverableCovered;
    out.push({
      id: "coverage",
      title: "Every deliverable requirement covered",
      detail:
        uncovered === 0
          ? `All ${coverage.deliverable} deliverable requirements are linked to a section.`
          : `${uncovered} of ${coverage.deliverable} deliverable requirements still uncovered.`,
      tone: uncovered === 0 ? "ok" : "red",
      href: "drafts",
    });
  }

  // Compliance acknowledged
  const compliance = matrixItems.filter(
    (m) => (m.type === "compliance" || m.type === "admin") && !m.notApplicable,
  );
  if (compliance.length > 0) {
    const pending = compliance.filter((m) => m.status !== "complete");
    out.push({
      id: "compliance",
      title: "Compliance items acknowledged",
      detail:
        pending.length === 0
          ? `All ${compliance.length} compliance / admin item${compliance.length === 1 ? "" : "s"} acknowledged.`
          : `${pending.length} of ${compliance.length} compliance / admin item${compliance.length === 1 ? "" : "s"} pending.`,
      tone: pending.length === 0 ? "ok" : "warn",
      href: "matrix",
    });
  }

  return out;
}

function formatLabel(format: string): string {
  if (format === "doc") return "Word document";
  if (format === "presentation") return "Presentation";
  if (format === "both") return "Both — doc + deck";
  return format;
}

// ---------------------------------------------------------------------------
// Row renderer
// ---------------------------------------------------------------------------

function ChecklistRow({ check, projectId }: { check: Check; projectId: string }) {
  const Icon = check.tone === "ok" ? CheckCircle2 : check.tone === "warn" ? AlertCircle : XCircle;
  const color = check.tone === "ok" ? "#22c55e" : check.tone === "warn" ? "#F97316" : "#ef4444";
  const bg =
    check.tone === "ok"
      ? "rgba(34,197,94,0.06)"
      : check.tone === "warn"
        ? "rgba(249,115,22,0.06)"
        : "rgba(239,68,68,0.06)";

  const inner = (
    <div
      className="rounded-xl border px-4 py-3 flex items-start gap-3"
      style={{
        backgroundColor: bg,
        borderColor: color + "33",
      }}
    >
      <Icon size={16} style={{ color }} className="flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white">{check.title}</p>
        <p className="text-xs mt-0.5" style={{ color: "#9CA3AF" }}>
          {check.detail}
        </p>
      </div>
      {check.href && <ArrowRight size={14} className="text-gray-400 flex-shrink-0 mt-1" />}
    </div>
  );

  if (check.href) {
    return <Link href={`/projects/${projectId}/${check.href}`}>{inner}</Link>;
  }
  return inner;
}
